import { useQuery } from '@apollo/client';
import isNil from 'lodash/isNil';
import { inferParserType, useQueryStates, Values } from 'nuqs';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSession } from '@/components/session/useSession';
import { GET_CUSTOM_FIELD_FILTER_KEYS } from '@/graphql/DataCollection.queries';
import { useEuTaxonomyComplianceOptions } from '@/hooks/useEuTaxonomyComplianceOptions';
import {
  BUILDING_FILTERS_SEARCH_PARAMS,
  RESET_ALL_BUILDING_FILTERS_SEARCH_PARAMS,
  RESET_BUILDING_MORE_FILTERS_SEARCH_PARAMS,
} from './search-params';
import { calculateFilterCount } from './utils';

export type Filters = Values<typeof BUILDING_FILTERS_SEARCH_PARAMS>;

type ResetType = 'local-state-only' | 'more-filters-only' | 'all-filters';

type TContext = {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  urlFilters: inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>;
  applyFilters: VoidFunction;
  resetFilters: (resetType: ResetType) => void;
  hasValidationError: boolean;
  setFieldValidationError: (key: string, hasError: boolean) => void;
};

const BuildingFiltersContext = createContext<TContext | null>(null);

const STORAGE_KEY_PREFIX = 'building-filters';

type Props = PropsWithChildren<{
  storageKey?: string;
}>;

export function BuildingFiltersProvider({ children, storageKey }: Props) {
  const [invalidFieldKeys, setInvalidFieldKeys] = useState<ReadonlySet<string>>(new Set());
  const hasValidationError = invalidFieldKeys.size > 0;
  const setFieldValidationError = useCallback((key: string, hasError: boolean) => {
    setInvalidFieldKeys((prev) => {
      const isTracked = prev.has(key);
      if (hasError === isTracked) return prev;

      const next = new Set(prev);
      if (hasError) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);
  const hasRestoredFromStorage = useRef(false);

  const [urlFilters, setUrlFilters] = useQueryStates(BUILDING_FILTERS_SEARCH_PARAMS);
  const [filters, setFilters] = useState<Filters>(urlFilters);

  const euTaxonomyComplianceOptions = useEuTaxonomyComplianceOptions();
  const {
    org: { id: orgId },
    featureFlags: { SLF_CHECK_COMPLIANT, CUSTOM_PROPERTIES },
  } = useSession();

  const getStoredFilters = (storageKey: string): Partial<Filters> | null => {
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${storageKey}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const storeFilters = (storageKey: string, filters: Filters) => {
    try {
      const hasAnyFilter = calculateFilterCount(filters, false) > 0;
      if (hasAnyFilter) {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}-${storageKey}`, JSON.stringify(filters));
      } else {
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}-${storageKey}`);
      }
    } catch {
      // Ignore localStorage errors
    }
  };

  // On mount: restore filters from localStorage if URL has no filters.
  // Important: do NOT set hasRestoredFromStorage.current = true here when we actually restore,
  // because both effects run in the same commit and setUrlFilters is async. The persist effect
  // must skip its first run (when urlFilters is still empty) to avoid removing stored filters.
  useEffect(() => {
    if (!storageKey || hasRestoredFromStorage.current) return;

    // Do not treat portfolioIds / economicUnitIds alone as "URL already has filters":
    // RedirectToStatusQuoFirstPortfolio and RedirectToStatusQuoFirstEconomicUnit inject those ids
    // on first paint. Skip restore only when "more filters" are set, or building scope (deep link).
    const hasMoreFiltersInUrl = calculateFilterCount(urlFilters, true) > 0;
    const hasBuildingScopeInUrl = (urlFilters.buildingIds?.length ?? 0) > 0;
    if (hasMoreFiltersInUrl || hasBuildingScopeInUrl) {
      hasRestoredFromStorage.current = true; // explicit URL filter state — unblock persist
      return;
    }

    const storedFilters = getStoredFilters(storageKey);
    if (storedFilters) {
      const urlPortfolioIds = urlFilters.portfolioIds;
      const urlEconomicUnitIds = urlFilters.economicUnitIds;
      const filtersToApply = {
        ...storedFilters,
        ...((urlPortfolioIds?.length ?? 0) > 0 ? { portfolioIds: urlPortfolioIds } : {}),
        ...((urlEconomicUnitIds?.length ?? 0) > 0 ? { economicUnitIds: urlEconomicUnitIds } : {}),
      };
      void setUrlFilters(filtersToApply);
      // hasRestoredFromStorage stays false — persist effect will skip its first run (urlFilters
      // is still empty) and set the ref to true, allowing subsequent runs to persist normally
    } else {
      hasRestoredFromStorage.current = true; // nothing to restore — unblock persist
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters to localStorage whenever URL filters change.
  // On the first run after a restore (ref is still false), skip persisting and flip the ref so
  // subsequent runs proceed normally. This prevents clearing localStorage before the restored
  // urlFilters value has propagated from the async setUrlFilters call above.
  useEffect(() => {
    if (!hasRestoredFromStorage.current) {
      hasRestoredFromStorage.current = true; // first run after restore — skip persist, unblock future runs
      return;
    }
    if (storageKey) {
      storeFilters(storageKey, urlFilters);
    }
  }, [storageKey, urlFilters]);

  // The EU Taxonomy compliance tiers depend on the org's data source. If the applied filter still
  // carries a tier that the current data source no longer supports (e.g. "Top 30%" after switching
  // to Drees & Sommer), the list would return zero buildings and the option is hidden so it can't be
  // deselected. Clear the whole euTaxonomyCompliance filter in that case. Self-heals on mount, so it
  // works even though the data source was changed on another page.
  useEffect(() => {
    const selected = urlFilters.euTaxonomyCompliance;
    if (!selected?.length) return;

    const hasUnsupportedTier = selected.some((tier) => !euTaxonomyComplianceOptions.includes(tier));
    if (hasUnsupportedTier) {
      void setUrlFilters({ euTaxonomyCompliance: null });
    }
  }, [euTaxonomyComplianceOptions, urlFilters.euTaxonomyCompliance, setUrlFilters]);

  // Without the SLF flag the filter is not rendered, so a value carried in the URL or restored from
  // localStorage would keep narrowing the building list with no visible control to clear it.
  useEffect(() => {
    if (SLF_CHECK_COMPLIANT || isNil(urlFilters.slfCompliant)) return;

    void setUrlFilters({ slfCompliant: null });
  }, [SLF_CHECK_COMPLIANT, urlFilters.slfCompliant, setUrlFilters]);

  // Same invisible-narrowing problem for custom-field filters: a filter whose custom field was
  // deleted (or whose feature flag is off) has no visible control and would silently return an
  // empty list. Validate applied filters against the org's current custom fields and drop stale ones.
  const appliedCustomFields = urlFilters.customFields;
  const { data: customFieldKeysData } = useQuery(GET_CUSTOM_FIELD_FILTER_KEYS, {
    variables: { orgId },
    skip: !CUSTOM_PROPERTIES || isNil(appliedCustomFields),
  });
  useEffect(() => {
    if (isNil(appliedCustomFields)) return;

    if (!CUSTOM_PROPERTIES) {
      void setUrlFilters({ customFields: null });
      return;
    }
    if (!customFieldKeysData) return;

    const validFilters = appliedCustomFields.filter((filter) =>
      customFieldKeysData.custom_field.some((field) => field.key === filter.key && field.entity_type === filter.entity),
    );
    if (validFilters.length !== appliedCustomFields.length) {
      void setUrlFilters({ customFields: validFilters.length > 0 ? validFilters : null });
    }
  }, [CUSTOM_PROPERTIES, appliedCustomFields, customFieldKeysData, setUrlFilters]);

  const applyFilters = useCallback(() => {
    if (!hasValidationError) {
      setUrlFilters(filters);
    }
  }, [filters, hasValidationError, setUrlFilters]);

  const resetFilters = useCallback(
    (resetType: ResetType) => {
      if (resetType === 'local-state-only') {
        setFilters(urlFilters);
        return;
      }

      const hasUrlFilters = calculateFilterCount(urlFilters, resetType === 'more-filters-only') > 0;
      if (resetType === 'more-filters-only') {
        if (hasUrlFilters) {
          setUrlFilters(RESET_BUILDING_MORE_FILTERS_SEARCH_PARAMS);
        } else {
          setFilters(urlFilters);
        }
        return;
      }

      if (hasUrlFilters) {
        setUrlFilters(RESET_ALL_BUILDING_FILTERS_SEARCH_PARAMS);
      } else {
        setFilters(urlFilters);
      }
    },
    [setFilters, setUrlFilters, urlFilters],
  );

  useEffect(() => {
    setFilters(urlFilters);
  }, [urlFilters]);

  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      urlFilters,
      applyFilters,
      resetFilters,
      hasValidationError,
      setFieldValidationError,
    }),
    [filters, setFilters, urlFilters, applyFilters, resetFilters, hasValidationError, setFieldValidationError],
  );

  return <BuildingFiltersContext.Provider value={contextValue}>{children}</BuildingFiltersContext.Provider>;
}

export const useBuildingFilters = () => {
  const context = useContext(BuildingFiltersContext);

  if (!context) {
    throw new Error('useBuildingFilters must be used within a BuildingFiltersProvider');
  }

  return context;
};
